import BaseController from "./BaseController";
import { container } from "tsyringe";
import { UsersService } from "../service/users/userService";
import "reflect-metadata";
import { UserDTO } from "../generated-client";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import { BusinessPartner, ProfitCenter, RoleEnum } from "@ang/shared-types";
import { BusinessPartnerService } from "../service/businessPartners/businessPartnersService";
import { SearchField$LiveChangeEvent } from "sap/m/SearchField";
import Table from "sap/m/Table";
import ListBinding from "sap/ui/model/ListBinding";
import { UserFilterService } from "../service/filter/UserFilterService";
import { ProfitCentersService } from "../service/profitCenter/profitCenterService";

export default class UserDetailsController extends BaseController {
	private readonly userService: UsersService = container.resolve(UsersService);
	private readonly businessPartnerService: BusinessPartnerService =
		container.resolve(BusinessPartnerService);
	private readonly profitCentersService: ProfitCentersService =
		container.resolve(ProfitCentersService);

	private oUsersModel: JSONModel;
	private createUserModel: JSONModel;
	private businessPartnersModel: JSONModel;
	private profitCodeModel: JSONModel;

	private addUserDialog: Dialog;

	private editUserDialog: Dialog;
	private userFilterService: UserFilterService = new UserFilterService();

	onInit(): void {
		this.getView().setBusy(true);
		void this.initializeModels(
			() => this.setUserCreateModel(),
			[
				() => this.setUserTable(),
				() => this.setBusinessPartnersModel(),
				() => this.setProfitCodeModel(),
			],
		).finally(() => {
			this.getView().setBusy(false);
		});
	}
	//Create User Dialog
	async onAddUserDialogPress(): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() =>
				!!this.createUserModel &&
				!!this.businessPartnersModel &&
				!!this.profitCodeModel,
			"User data is still loading. Please wait and try again.",
		);
		if (!isReady) {
			return;
		}

		this.addUserDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.users.CreateUsersDialog",
		})) as Dialog;

		this.addUserDialog
			.setModel(this.createUserModel, "createUserModel")
			.setModel(this.businessPartnersModel, "businessPartnersModel")
			.setModel(this.profitCodeModel, "profitCodeModel");

		this.addUserDialog.open();
	}

	onAddUserDialogClose(): void {
		this.setUserCreateModel();
		(this.byId("AddUserDialog") as Dialog)?.close();

		this.setUserTable(); // Refresh user table automatically
	}

	async onAddUserDialogSubmit(): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() => !!this.createUserModel,
			"Create user model is not ready yet.",
		);
		if (!isReady) {
			return;
		}

		const userDto: UserDTO = this.createUserModel.getData() as UserDTO;

		// Validate user data
		const validationError = await this.validateUserData(userDto);
		if (validationError) {
			this.showWarningMessage(validationError);
			return;
		}

		console.log("User DTO to create:", userDto);

		const success = await this.executeWithErrorHandling(
			() => this.createUser(userDto),
			await this.getText("creatingUser"),
			await this.getText("userCreatedSuccessfully"),
			await this.getText("failedToCreateUser"),
		);

		if (success !== null) {
			this.onAddUserDialogClose();
		}
	}

	async onEditUserDialogPress(event: any): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() =>
				!!this.profitCodeModel &&
				!!this.businessPartnersModel &&
				!!this.oUsersModel,
			"Edit user data is still loading. Please wait and try again.",
		);
		if (!isReady) {
			return;
		}

		this.editUserDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.users.EditUserDialog",
		})) as Dialog;

		const rowData = event
			.getSource()
			.getBindingContext("usersModel")
			.getObject();

		console.log("Row data for edit:", rowData);
		this.editUserDialog
			.setModel(new JSONModel(rowData), "editUserModel")
			.setModel(this.profitCodeModel, "profitCodeModel")
			.setModel(this.businessPartnersModel, "businessPartnersModel");

		this.editUserDialog.open();
	}

	async setProfitCodeModel(): Promise<void> {
		const profitCodes = await this.executeWithErrorHandling(
			() => this.getProfitCodes(),
			undefined,
			undefined,
			await this.getText("failedToLoadProfitCodes"),
		);

		if (profitCodes) {
			this.profitCodeModel = new JSONModel(profitCodes);
			console.log("Model:", this.profitCodeModel);
			this.getView().setModel(this.profitCodeModel, "profitCodeModel");
		} else {
			// Set empty model on error
			this.profitCodeModel = new JSONModel([]);
			this.getView().setModel(this.profitCodeModel, "profitCodeModel");
		}
	}

	async getProfitCodes(): Promise<ProfitCenter[]> {
		const response = await this.profitCentersService.getProfitCenters();
		console.log("ProfitData: ", response);
		// Extract the data array from the response object
		return (response as any)?.data || [];
	}

	async onEditUserDialogSubmit(): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() => !!this.editUserDialog,
			"Edit user model is not ready yet.",
		);
		if (!isReady) {
			return;
		}

		const userDto: UserDTO = (
			this.editUserDialog.getModel("editUserModel") as JSONModel
		).getData() as UserDTO;

		// Validate user data
		const validationError = await this.validateUserData(userDto, true);
		if (validationError) {
			this.showWarningMessage(validationError);
			return;
		}

		console.log("User DTO to edit:", userDto);

		const success = await this.executeWithErrorHandling(
			() => this.userService.updateUser(userDto),
			await this.getText("updatingUser"),
			await this.getText("userUpdatedSuccessfully"),
			await this.getText("failedToUpdateUser"),
		);

		if (success !== null) {
			this.onEditUserDialogClose();
			this.setUserTable(); // Refresh user list
		}
	}

	onEditUserDialogClose(): void {
		(this.byId("EditUserDialog") as Dialog)?.close();
	}

	onUserSearch(event: SearchField$LiveChangeEvent): void {
		const searchValue = event.getParameter("newValue");
		console.log("Search value:", searchValue);

		const oBinding = (this.byId("usersTable") as Table).getBinding(
			"items",
		) as ListBinding;
		if (oBinding) {
			if (searchValue && searchValue.trim() !== "") {
				oBinding.filter(this.userFilterService.createUserFilter(searchValue));
			}
		}
	}

	private refreshTable(): void {
		// Refresh the table by reloading the user data
		this.setUserTable();
	}

	private async setUserTable(): Promise<void> {
		const users = await this.executeWithErrorHandling(
			() => this.getUserData(),
			await this.getText("loadingUsers"),
			undefined, // No success message for background loading
			await this.getText("failedToLoadUsers"),
		);

		if (users) {
			this.oUsersModel = new JSONModel(users);
			this.getView().setModel(this.oUsersModel, "usersModel");
		} else {
			// Set empty model on error
			this.oUsersModel = new JSONModel([]);
			this.getView().setModel(this.oUsersModel, "usersModel");
		}
	}

	private setUserCreateModel(): void {
		const userModel: UserDTO = {
			email: "",
			password: "",
			name: "",
			surname: "",
			username: "",
			company: "",
			role: RoleEnum.USER,
			cardCode: "",
			profitCode: "",
		};
		this.createUserModel = new JSONModel(userModel);
	}

	private async setBusinessPartnersModel(): Promise<void> {
		const businessPartners = await this.executeWithErrorHandling(
			() => this.getBusinessPartners(),
			undefined,
			undefined,
			await this.getText("failedToLoadBusinessPartners"),
		);

		if (businessPartners) {
			this.businessPartnersModel = new JSONModel(businessPartners);
			this.getView().setModel(
				this.businessPartnersModel,
				"businessPartnersModel",
			);
		} else {
			// Set empty model on error
			this.businessPartnersModel = new JSONModel([]);
			this.getView().setModel(
				this.businessPartnersModel,
				"businessPartnersModel",
			);
		}
	}

	private async getBusinessPartners(): Promise<BusinessPartner[]> {
		const response = await this.businessPartnerService.getBusinessPartners();
		return response.data as unknown as BusinessPartner[];
	}

	private async getUserData(): Promise<UserDTO[]> {
		const response = await this.userService.getAllUsers();
		console.log("Fetched user data:", response);
		return response;
	}

	private async createUser(userDto: UserDTO): Promise<void> {
		await this.userService.createUser(userDto);
	}

	/**
	 * Validates user data before submission
	 * @param userData The user data to validate
	 * @param isEdit Whether this is an edit operation (password is optional)
	 * @returns Error message if validation fails, null if valid
	 */
	private async validateUserData(
		userData: UserDTO,
		isEdit: boolean = false,
	): Promise<string | null> {
		if (!userData.email || userData.email.trim() === "") {
			return await this.getText("usernameRequired");
		}

		// Basic email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(userData.email.trim())) {
			return await this.getText("invalidEmail");
		}

		if (!userData.username || userData.username.trim() === "") {
			return await this.getText("usernameRequired");
		}

		if (!userData.name || userData.name.trim() === "") {
			return await this.getText("nameRequired");
		}

		if (!userData.surname || userData.surname.trim() === "") {
			return await this.getText("surnameRequired");
		}

		// Password is only required for new users
		if (!isEdit && (!userData.password || userData.password.trim() === "")) {
			return await this.getText("passwordRequired");
		}

		// Password strength validation (only if password is provided)
		if (userData.password && userData.password.trim() !== "") {
			if (userData.password.length < 6) {
				return await this.getText("passwordStrength");
			}
		}

		if (!userData.cardCode || userData.cardCode.trim() === "") {
			return await this.getText("customerCodeRequired");
		}

		const normalizedCardCode = userData.cardCode.trim().toLowerCase();
		if (normalizedCardCode === "unknown") {
			return await this.getText("customerCodeRequired");
		}

		if (!userData.role) {
			return await this.getText("roleRequired");
		}

		if (!userData.profitCode || userData.profitCode.trim() === "") {
			return await this.getText("profitCodeRequired");
		}

		return null; // Validation passed
	}
}
